import type { AdminPage } from '@neighbourhood-munchies/shared-types';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useNavigation } from '../../hooks/useNavigation';
import { useOrientation } from '../../hooks/useOrientation';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import {
  AdsIcon,
  AnalyticsIcon,
  DashboardIcon,
  LiveOrdersIcon,
  MapIcon,
  OrdersIcon,
  RestaurantIcon,
  SettingsIcon,
  UsersIcon,
} from '../ui/Icons';
import { MobileBackdrop } from './MobileBackdrop';
import { NavigationHeader } from './NavigationHeader';
import { NavigationItem } from './NavigationItem';
import { UserProfile } from './UserProfile';

interface SubNavItemConfig {
  id: string;
  label: string;
  path: string;
  permission: AdminPage;
}

interface NavigationItemConfig {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string; size?: 'sm' | 'md' | 'lg' }>;
  badge?: number | string;
  shortcut: string;
  permission: AdminPage;
  subItems?: SubNavItemConfig[];
}

interface SideNavigationProps {
  user?: {
    name?: string;
    email?: string;
    avatar?: string;
    role?: string;
  } | null;
  onLogout: () => void;
  pendingOrdersCount?: number;
}

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

// Navigation configuration
const navigationItems: NavigationItemConfig[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: DashboardIcon,
    shortcut: 'Alt+1',
    permission: 'dashboard',
  },
  {
    id: 'live-orders',
    label: 'Live Orders',
    path: '/live-orders',
    icon: LiveOrdersIcon,
    shortcut: 'Alt+4',
    permission: 'live-orders',
  },
  {
    id: 'restaurants',
    label: 'Restaurants',
    path: '/restaurants',
    icon: RestaurantIcon,
    shortcut: 'Alt+2',
    permission: 'restaurants',
  },
  {
    id: 'orders',
    label: 'Orders',
    path: '/orders',
    icon: OrdersIcon,
    shortcut: 'Alt+3',
    permission: 'order-management',
  },
  {
    id: 'ads',
    label: 'Advertisements',
    path: '/advertisements',
    icon: AdsIcon,
    shortcut: 'Alt+8',
    permission: 'advertisements',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    path: '/analytics',
    icon: AnalyticsIcon,
    shortcut: 'Alt+7',
    permission: 'analytics',
    subItems: [
      {
        id: 'analytics-overview',
        label: 'Overview',
        path: '/analytics',
        permission: 'analytics',
      },
      {
        id: 'restaurant-performance',
        label: 'Top Performers Report',
        path: '/analytics/restaurant-performance',
        permission: 'analytics',
      },
      {
        id: 'analytics-customers',
        label: 'Customers',
        path: '/analytics/customers',
        permission: 'analytics',
      },
      {
        id: 'analytics-cancellations',
        label: 'Cancellations & Quality',
        path: '/analytics/cancellations',
        permission: 'analytics',
      },
      {
        id: 'analytics-delivery',
        label: 'Delivery Breakdown',
        path: '/analytics/delivery',
        permission: 'analytics',
      },
    ],
  },
  {
    id: 'zones',
    label: 'Zone Management',
    path: '/zones',
    icon: MapIcon,
    shortcut: 'Alt+5',
    permission: 'zones',
  },
  {
    id: 'customer-users',
    label: 'Customer Management',
    path: '/customer-management',
    icon: UsersIcon,
    shortcut: 'Alt+6',
    permission: 'customer-management',
  },
  {
    id: 'staff-users',
    label: 'Staff Management',
    path: '/staff-management',
    icon: UsersIcon,
    shortcut: 'Alt+9',
    permission: 'staff-management',
  },
  {
    id: 'system-settings',
    label: 'System Settings',
    path: '/system-settings',
    icon: SettingsIcon,
    shortcut: 'Alt+0',
    permission: 'system-settings',
  },
];

export const SideNavigation: React.FC<SideNavigationProps> = ({
  user,
  onLogout,
  pendingOrdersCount,
}) => {
  const navRef = useRef<HTMLElement>(null);
  const { hasPermission } = useAuth();
  const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});
  const {
    isExpanded,
    isMobileMenuOpen,
    activeRoute,
    toggleExpanded,
    toggleMobileMenu,
    closeMobileMenu,
    navigateTo,
  } = useNavigation();

  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const orientation = useOrientation();

  const shouldUseMobileMenu = isMobile || (isTablet && orientation === 'portrait');

  // On desktop, isExpanded drives collapse/expand. On mobile it's always "full".
  const isCollapsed = !shouldUseMobileMenu && !isExpanded;
  const navWidth = shouldUseMobileMenu ? 280 : isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const filteredNavigationItems = navigationItems.filter((item) => hasPermission(item.permission));

  useSwipeGesture(navRef, {
    onSwipeLeft: () => {
      if (shouldUseMobileMenu && isMobileMenuOpen) closeMobileMenu();
    },
    onSwipeRight: () => {
      if (shouldUseMobileMenu && !isMobileMenuOpen) toggleMobileMenu();
    },
    threshold: 50,
    restraint: 100,
    allowedTime: 300,
  });

  useKeyboardShortcuts(
    shouldUseMobileMenu ? toggleMobileMenu : toggleExpanded,
    navigateTo,
    filteredNavigationItems
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const isMobileNow = width < 768;
        const isTabletNow = width >= 768 && width < 1024;
        const isPortraitNow = height > width;
        if (
          (!isMobileNow && isMobileMenuOpen) ||
          (isTabletNow && !isPortraitNow && isMobileMenuOpen)
        ) {
          closeMobileMenu();
        }
      }, 150);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timeoutId);
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (!shouldUseMobileMenu || !isMobileMenuOpen || !navRef.current) return;
    const container = navRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };
    container.addEventListener('keydown', handleTab);
    firstElement?.focus();
    return () => container.removeEventListener('keydown', handleTab);
  }, [shouldUseMobileMenu, isMobileMenuOpen]);

  const navItemsWithBadge = filteredNavigationItems.map((item) =>
    item.id === 'live-orders' && pendingOrdersCount && pendingOrdersCount > 0
      ? { ...item, badge: pendingOrdersCount }
      : item
  );

  const isVisible = shouldUseMobileMenu ? isMobileMenuOpen : true;

  return (
    <>
      {shouldUseMobileMenu && (
        <MobileBackdrop isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      )}

      <nav
        ref={navRef}
        id="side-navigation"
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={shouldUseMobileMenu && !isVisible}
        className={`
          fixed top-0 left-0 h-screen
          bg-white/80 backdrop-blur-xl
          flex flex-col
          transition-all duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${
            shouldUseMobileMenu
              ? isVisible
                ? 'translate-x-0 z-50'
                : '-translate-x-full z-50 pointer-events-none'
              : 'z-30'
          }
        `}
        style={{ width: `${navWidth}px`, transitionProperty: 'width, transform' }}
      >
        <NavigationHeader
          isExpanded={shouldUseMobileMenu || isExpanded}
          onToggle={shouldUseMobileMenu ? closeMobileMenu : toggleExpanded}
          isMobileMode={shouldUseMobileMenu}
        />

        <div className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto overflow-x-hidden mt-2">
          {navItemsWithBadge.map((item) => {
            const Icon = item.icon;
            const visibleSubItems = (item.subItems || []).filter((sub) =>
              hasPermission(sub.permission)
            );
            const hasDropdown = visibleSubItems.length > 0;
            const isOnSubRoute = hasDropdown && activeRoute.startsWith(item.path + '/');
            const isActive = activeRoute === item.path || isOnSubRoute;
            // openDropdowns[item.id]: undefined = auto (follow isOnSubRoute), true = forced open, false = explicitly closed
            const isOpen =
              !isCollapsed &&
              (openDropdowns[item.id] === true ||
                (openDropdowns[item.id] !== false && isOnSubRoute));

            if (hasDropdown) {
              return (
                <React.Fragment key={item.id}>
                  {/* Dropdown parent button */}
                  <button
                    type="button"
                    title={isCollapsed ? item.label : undefined}
                    onClick={() => {
                      if (isCollapsed) {
                        // Expand the nav first, then open dropdown
                        toggleExpanded();
                        setOpenDropdowns((prev) => ({ ...prev, [item.id]: true }));
                      } else {
                        setOpenDropdowns((prev) => ({ ...prev, [item.id]: !isOpen }));
                      }
                    }}
                    className={`
                      group relative flex items-center w-full h-11 rounded-xl transition-all duration-150
                      ${isCollapsed ? 'justify-center px-2' : 'px-3 gap-3'}
                      ${
                        isActive
                          ? 'bg-gray-900 text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }
                      focus:outline-none focus:ring-2 focus:ring-admin-orange-500 focus:ring-offset-2 focus:ring-offset-white
                    `}
                  >
                    <span
                      className={`flex-shrink-0 ${
                        isActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-900'
                      }`}
                    >
                      <Icon size="md" />
                    </span>
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-sm font-medium truncate text-left">
                          {item.label}
                        </span>
                        <svg
                          className={`w-4 h-4 flex-shrink-0 transition-transform duration-150 ${
                            isOpen ? 'rotate-180' : ''
                          } ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </button>

                  {/* Sub-items — only when expanded and open */}
                  {isOpen && (
                    <div className="space-y-0.5 mt-0.5">
                      {visibleSubItems.map((sub) => {
                        const isSubActive = activeRoute === sub.path;
                        return (
                          <Link
                            key={sub.id}
                            to={sub.path}
                            onClick={() => navigateTo(sub.path)}
                            className={`
                              flex items-center h-9 rounded-xl pl-3 pr-3 gap-3 text-sm transition-all duration-150
                              focus:outline-none focus:ring-2 focus:ring-admin-orange-500 focus:ring-offset-2
                              ${
                                isSubActive
                                  ? 'bg-gray-100 text-gray-900 font-medium'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                              }
                            `}
                          >
                            {/* Icon-width spacer so text aligns with parent label */}
                            <span className="flex-shrink-0 w-5 flex items-center justify-center">
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  isSubActive ? 'bg-gray-900' : 'bg-gray-400'
                                }`}
                              />
                            </span>
                            <span className="truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            }

            return (
              <NavigationItem
                key={item.id}
                icon={<Icon size="md" />}
                label={item.label}
                path={item.path}
                isActive={isActive}
                isExpanded={!isCollapsed}
                collapsed={isCollapsed}
                onClick={() => navigateTo(item.path)}
                badge={item.badge}
                shortcut={item.shortcut}
              />
            );
          })}
        </div>

        <UserProfile user={user} isExpanded={!isCollapsed} onLogout={onLogout} />
      </nav>
    </>
  );
};
